import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { deleteSuiteRun, updateSuiteRun } from '@/features/executions/data/suiteRuns';
import { buildSuiteRunParentInvalidations } from '@/features/executions/queries/executionInvalidationFilters';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useNotifications } from '@/providers/notificationHooks';
import { getToastMessage } from '@/shared/copy/toasts';
import { useApiErrorMessage } from '@/shared/data/errors';
import { updateTagColor, type TagColor } from '@/shared/data/tags';
import {
  invalidateBuilds,
  gridPaths,
  invalidateGridQuery,
  invalidateMetrics,
  invalidateSuites,
  invalidateSuiteRunDetail,
  invalidateSuiteRuns,
  invalidateTests,
  invalidateTestRuns,
  queryKeys,
  useInvalidateQueries,
} from '@/shared/hooks';
import type { SuiteRunDto } from '@flowqa/shared';

type DeleteTarget = {
  id: string;
  name: string;
};

type UseSuiteRunsTableRowActionsParams = { selectedProjectId: string | null };

type UseSuiteRunsTableRowActionsResult = {
  deleteLoading: boolean;
  deleteRowPendingId: string | null;
  deleteTarget: DeleteTarget | null;
  onDeleteClose: () => void;
  onDeleteConfirm: () => void;
  onDeleteRow: (row: SuiteRunDto) => void;
  onUpdateRowTagsColor: (input: { row: SuiteRunDto; tags: string[]; colors: TagColor[] }) => void;
  updateRowTagsColorPendingId: string | null;
};

export const useSuiteRunsTableRowActions = ({
  selectedProjectId,
}: UseSuiteRunsTableRowActionsParams): UseSuiteRunsTableRowActionsResult => {
  const queryClient = useQueryClient();
  const invalidateQueries = useInvalidateQueries();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { notify } = useNotifications();
  const toErrorMessage = useApiErrorMessage();
  const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
  const [deleteTargetRow, setDeleteTargetRow] = useState<SuiteRunDto | null>(null);
  const [updatingTagRowId, setUpdatingTagRowId] = useState<string | null>(null);

  const deleteMutation = useMutation({
    mutationFn: async (input: { id: string }) =>
      await deleteSuiteRun(input, selectedOrganisationId),
    onSuccess: async (_deleted, input) => {
      const parentInvalidations = deleteTargetRow
        ? buildSuiteRunParentInvalidations(deleteTargetRow)
        : [];
      await invalidateQueries([
        invalidateSuiteRuns(selectedProjectId ?? null, selectedOrganisationId ?? null),
        invalidateSuiteRunDetail(input.id, selectedOrganisationId ?? null),
        invalidateSuites(selectedProjectId ?? null, selectedOrganisationId ?? null),
        invalidateTestRuns(selectedProjectId ?? null, selectedOrganisationId ?? null),
        invalidateTests(selectedProjectId ?? null, selectedOrganisationId ?? null),
        invalidateBuilds(selectedProjectId ?? null, selectedOrganisationId ?? null),
        invalidateMetrics(selectedOrganisationId ?? null, selectedProjectId ?? null),
        invalidateGridQuery(gridPaths.suiteRuns),
        invalidateGridQuery(gridPaths.testRuns),
        invalidateGridQuery(gridPaths.suites),
        invalidateGridQuery(gridPaths.tests),
        invalidateGridQuery(gridPaths.builds),
        ...parentInvalidations,
      ]);
      notify({
        message: getToastMessage('entity.deleted', {
          entity: 'suite run',
          name: deleteTarget?.name ?? input.id,
        }),
        tone: 'green',
      });
      setDeleteTarget(null);
      setDeleteTargetRow(null);
    },
    onError: (error) => {
      notify({ message: toErrorMessage(error), tone: 'red' });
    },
  });

  const updateTagColorMutation = useMutation({
    mutationFn: async (input: { tags: string[]; colors: TagColor[] }) => {
      const updates = input.tags
        .map((name, index) => ({ name: name.trim(), color: input.colors[index] ?? 'neutral' }))
        .filter((item) => item.name.length > 0);
      if (updates.length === 0) return;
      await Promise.all(
        updates.map(async (item) => {
          await updateTagColor(item, selectedOrganisationId ?? undefined);
        }),
      );
    },
  });

  const onDeleteRow = useCallback((row: SuiteRunDto) => {
    const name = row.suiteName?.trim() ?? row.name?.trim() ?? row.id;
    setDeleteTarget({ id: row.id, name });
    setDeleteTargetRow(row);
  }, []);

  const onDeleteClose = useCallback(() => {
    if (deleteMutation.isPending) return;
    setDeleteTarget(null);
    setDeleteTargetRow(null);
  }, [deleteMutation.isPending]);

  const onDeleteConfirm = useCallback(() => {
    if (!deleteTarget) return;
    deleteMutation.mutate({ id: deleteTarget.id });
  }, [deleteMutation, deleteTarget]);

  const onUpdateRowTagsColor = useCallback(
    ({ row, tags, colors }: { row: SuiteRunDto; tags: string[]; colors: TagColor[] }) => {
      const normalizedTags = Array.from(
        new Set(tags.map((tag) => tag.trim()).filter((tag) => tag.length > 0)),
      );
      const normalizedColors = colors.slice(0, normalizedTags.length);
      setUpdatingTagRowId(row.id);
      void (async () => {
        try {
          await updateSuiteRun({ id: row.id, tags: normalizedTags }, selectedOrganisationId);
          if (normalizedTags.length > 0) {
            await updateTagColorMutation.mutateAsync({
              tags: normalizedTags,
              colors: normalizedColors,
            });
          }
          notify({ message: getToastMessage('tags.updated'), tone: 'green' });
          await queryClient.invalidateQueries({
            queryKey: queryKeys.tagOptions(selectedProjectId, selectedOrganisationId ?? null),
          });
          await invalidateQueries([
            invalidateSuiteRuns(selectedProjectId ?? null, selectedOrganisationId ?? null),
            invalidateSuiteRunDetail(row.id, selectedOrganisationId ?? null),
            invalidateTestRuns(selectedProjectId ?? null, selectedOrganisationId ?? null),
            invalidateGridQuery(gridPaths.suiteRuns),
            invalidateGridQuery(gridPaths.testRuns),
          ]);
        } catch (error) {
          notify({ message: toErrorMessage(error), tone: 'red' });
        } finally {
          setUpdatingTagRowId(null);
        }
      })();
    },
    [
      invalidateQueries,
      notify,
      queryClient,
      selectedOrganisationId,
      selectedProjectId,
      toErrorMessage,
      updateTagColorMutation,
    ],
  );

  return {
    deleteLoading: deleteMutation.isPending,
    deleteRowPendingId: deleteMutation.isPending ? (deleteTarget?.id ?? null) : null,
    deleteTarget,
    onDeleteClose,
    onDeleteConfirm,
    onDeleteRow,
    onUpdateRowTagsColor,
    updateRowTagsColorPendingId: updatingTagRowId,
  };
};
